import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { DocumentType, getDocumentTypeLabel } from '../../api/documents';
import { getBirds, Bird } from '../../api/birds';
import { healthTestsApi, HealthTest } from '../../api/health-tests';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface CreateDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
}

const CreateDocumentModal: React.FC<CreateDocumentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    type: DocumentType.OTHER,
    fileUrl: '',
    description: '',
    tags: [] as string[],
    metadata: {
      issueDate: '',
      expiryDate: '',
      issuer: '',
      reference: '',
    },
    name: '',
    mimeType: '',
    fileSize: 0,
    birdId: '',
    healthTestId: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadMethod, setUploadMethod] = useState<'file' | 'url'>('file');
  const [isDragging, setIsDragging] = useState(false);
  
  // Bird and Health Test selectors
  const [birds, setBirds] = useState<Bird[]>([]);
  const [healthTests, setHealthTests] = useState<HealthTest[]>([]);
  const [loadingBirds, setLoadingBirds] = useState(false);
  const [loadingHealthTests, setLoadingHealthTests] = useState(false);
  const [selectedBirdId, setSelectedBirdId] = useState<string>('');
  const [selectedHealthTestId, setSelectedHealthTestId] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let submitData: any = {
        ...formData,
        fileSize: formData.fileSize || undefined,
        birdId: formData.birdId || undefined,
        healthTestId: formData.healthTestId || undefined,
        metadata: Object.values(formData.metadata).some(v => v) ? formData.metadata : undefined,
      };

      // If file is selected, prepare FormData for file upload
      if (uploadMethod === 'file' && selectedFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('file', selectedFile);
        formDataToSend.append('title', submitData.title);
        formDataToSend.append('type', submitData.type);
        // fileUrl is optional when a file is uploaded - the API will generate it
        if (submitData.description) formDataToSend.append('description', submitData.description);
        if (submitData.tags && submitData.tags.length > 0) {
          submitData.tags.forEach((tag: string) => formDataToSend.append('tags[]', tag));
        }
        if (submitData.metadata) {
          Object.entries(submitData.metadata).forEach(([key, value]) => {
            if (value) formDataToSend.append(`metadata[${key}]`, value as string);
          });
        }
        if (submitData.name) formDataToSend.append('name', submitData.name);
        if (submitData.mimeType) formDataToSend.append('mimeType', submitData.mimeType);
        if (submitData.fileSize) formDataToSend.append('fileSize', submitData.fileSize.toString());
        if (submitData.birdId) formDataToSend.append('birdId', submitData.birdId);
        if (submitData.healthTestId) formDataToSend.append('healthTestId', submitData.healthTestId);
        
        // Use FormData for file upload
        submitData = formDataToSend;
      } else if (uploadMethod === 'url' && !formData.fileUrl) {
        alert('Please provide either a file or a file URL');
        setLoading(false);
        return;
      }

      await onSubmit(submitData);
      
      // Reset form
      setFormData({
        title: '',
        type: DocumentType.OTHER,
        fileUrl: '',
        description: '',
        tags: [],
        metadata: {
          issueDate: '',
          expiryDate: '',
          issuer: '',
          reference: '',
        },
        name: '',
        mimeType: '',
        fileSize: 0,
        birdId: '',
        healthTestId: '',
      });
      setTagInput('');
      setSelectedFile(null);
      setUploadMethod('file');
    } catch (error) {
      console.error('Error creating document:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Load birds and health tests
  useEffect(() => {
    if (isOpen) {
      loadBirds();
      loadHealthTests();
    }
  }, [isOpen]);

  const loadBirds = useCallback(async () => {
    try {
      setLoadingBirds(true);
      const birdsData = await getBirds();
      setBirds(birdsData);
    } catch (error) {
      console.error('Error loading birds:', error);
    } finally {
      setLoadingBirds(false);
    }
  }, []);

  const loadHealthTests = useCallback(async () => {
    try {
      setLoadingHealthTests(true);
      const response = await healthTestsApi.getHealthTests();
      const healthTestsData = Array.isArray(response.data) ? response.data : [];
      setHealthTests(healthTestsData);
    } catch (error) {
      console.error('Error loading health tests:', error);
    } finally {
      setLoadingHealthTests(false);
    }
  }, []);

  // Filter health tests by selected bird
  const filteredHealthTests = selectedBirdId
    ? healthTests.filter(ht => ht.birdId === selectedBirdId)
    : healthTests;

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setFormData(prev => ({
        ...prev,
        name: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }));
      setUploadMethod('file');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setFormData(prev => ({
        ...prev,
        name: file.name,
        mimeType: file.type,
        fileSize: file.size,
      }));
    }
  };

  // Update formData when bird/healthTest selectors change
  useEffect(() => {
    setFormData(prev => ({ ...prev, birdId: selectedBirdId || '' }));
  }, [selectedBirdId]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, healthTestId: selectedHealthTestId || '' }));
  }, [selectedHealthTestId]);

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Add New Document" onClose={onClose} />
      <form id="create-document-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter document title"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type */}
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-slate-700">
                      Document Type *
                    </label>
                    <select
                      id="type"
                      required
                      value={formData.type}
                      onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as DocumentType }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    >
                      {Object.values(DocumentType).map((type) => (
                        <option key={type} value={type}>
                          {getDocumentTypeLabel(type)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* File Size */}
                  <div>
                    <label htmlFor="fileSize" className="block text-sm font-medium text-slate-700">
                      File Size (bytes)
                    </label>
                    <input
                      type="number"
                      id="fileSize"
                      min="0"
                      value={formData.fileSize || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, fileSize: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Upload Method Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Upload Method
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="file"
                        checked={uploadMethod === 'file'}
                        onChange={(e) => setUploadMethod(e.target.value as 'file' | 'url')}
                        className="mr-2"
                      />
                      Upload File
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="url"
                        checked={uploadMethod === 'url'}
                        onChange={(e) => setUploadMethod(e.target.value as 'file' | 'url')}
                        className="mr-2"
                      />
                      File URL
                    </label>
                  </div>
                </div>

                {/* File Upload or URL Input */}
                {uploadMethod === 'file' ? (
                  <div>
                    <label htmlFor="file" className="block text-sm font-medium text-slate-700 mb-2">
                      Document File *
                    </label>
                    <div
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                        isDragging
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="space-y-1 text-center">
                        <svg
                          className="mx-auto h-12 w-12 text-slate-400"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                          aria-hidden="true"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <div className="flex text-sm text-slate-600">
                          <label
                            htmlFor="file"
                            className="relative cursor-pointer bg-white rounded-md font-medium text-cyan-600 hover:text-cyan-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-cyan-500"
                          >
                            <span>Upload a file</span>
                            <input
                              id="file"
                              name="file"
                              type="file"
                              className="sr-only"
                              required={uploadMethod === 'file'}
                              onChange={handleFileChange}
                              accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-slate-500">
                          PDF, DOC, DOCX, TXT, XLS, XLSX, JPG, PNG, GIF (Max 50MB)
                        </p>
                      </div>
                    </div>
                    {selectedFile && (
                      <div className="mt-2 p-3 bg-slate-50/80 rounded-md">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg className="h-5 w-5 text-slate-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
                              <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedFile(null);
                              setFormData(prev => ({
                                ...prev,
                                name: '',
                                mimeType: '',
                                fileSize: 0,
                              }));
                            }}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label htmlFor="fileUrl" className="block text-sm font-medium text-slate-700">
                      File URL *
                    </label>
                    <input
                      type="url"
                      id="fileUrl"
                      required={uploadMethod === 'url'}
                      value={formData.fileUrl}
                      onChange={(e) => setFormData(prev => ({ ...prev, fileUrl: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="https://example.com/document.pdf"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* File Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                      File Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="document.pdf"
                    />
                  </div>

                  {/* MIME Type */}
                  <div>
                    <label htmlFor="mimeType" className="block text-sm font-medium text-slate-700">
                      MIME Type
                    </label>
                    <input
                      type="text"
                      id="mimeType"
                      value={formData.mimeType}
                      onChange={(e) => setFormData(prev => ({ ...prev, mimeType: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="application/pdf"
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-slate-700">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Document description..."
                  />
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
                    Tags
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 min-w-0 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Add tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500 focus:outline-none focus:bg-cyan-500 focus:text-white"
                          >
                            <XMarkIcon className="h-2 w-2" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Metadata (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="issueDate" className="block text-sm font-medium text-slate-700">
                        Issue Date
                      </label>
                      <input
                        type="date"
                        id="issueDate"
                        value={formData.metadata.issueDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, issueDate: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="expiryDate" className="block text-sm font-medium text-slate-700">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        id="expiryDate"
                        value={formData.metadata.expiryDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, expiryDate: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="issuer" className="block text-sm font-medium text-slate-700">
                        Issuer
                      </label>
                      <input
                        type="text"
                        id="issuer"
                        value={formData.metadata.issuer}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, issuer: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Document issuer"
                      />
                    </div>

                    <div>
                      <label htmlFor="reference" className="block text-sm font-medium text-slate-700">
                        Reference Number
                      </label>
                      <input
                        type="text"
                        id="reference"
                        value={formData.metadata.reference}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          metadata: { ...prev.metadata, reference: e.target.value }
                        }))}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                        placeholder="Reference number"
                      />
                    </div>
                  </div>
                </div>

                {/* Optional Associations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700">
                      Associate with Bird (Optional)
                    </label>
                    <select
                      id="birdId"
                      value={selectedBirdId}
                      onChange={(e) => setSelectedBirdId(e.target.value)}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    >
                      <option value="">None</option>
                      {loadingBirds ? (
                        <option disabled>Loading birds...</option>
                      ) : (
                        birds.map((bird) => (
                          <option key={bird.id} value={bird.id}>
                            {bird.bandId} {bird.species?.commonName ? `- ${bird.species.commonName}` : ''}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="healthTestId" className="block text-sm font-medium text-slate-700">
                      Associate with Health Test (Optional)
                    </label>
                    <select
                      id="healthTestId"
                      value={selectedHealthTestId}
                      onChange={(e) => setSelectedHealthTestId(e.target.value)}
                      disabled={!selectedBirdId}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">None {!selectedBirdId && '(Select a bird first)'}</option>
                      {loadingHealthTests ? (
                        <option disabled>Loading health tests...</option>
                      ) : selectedBirdId && filteredHealthTests.length === 0 ? (
                        <option disabled>No health tests found for selected bird</option>
                      ) : (
                        filteredHealthTests.map((healthTest) => (
                          <option key={healthTest.id} value={healthTest.id}>
                            {healthTest.testName} - {new Date(healthTest.testDate).toLocaleDateString()}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={onClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="create-document-form"
            disabled={loading}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Document'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default CreateDocumentModal; 